import { LookupCombobox } from '../../components/ui/lookup-combobox';
import { CenteredDialog } from '../../components/ui/centered-dialog';
import { OmiePendenciasCentralPanel } from './omie-pendencias-central-panel';
// apps/web/src/pages/pcp/omie-pendencias-page.tsx — Fase 04, Plano 09 (OMIE-03..06, D-05..D-07, D-12..D-16).
import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileCheck2,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  Send,
  Shield,
  ShieldAlert,
  ShieldCheck,
  UserCheck,
} from 'lucide-react';
import type {
  EtapaFiscalTipo,
  OmiePendencia,
  OmiePilotoAprovacao,
  OmiePilotoCliente,
  OmiePilotoStatusResponse,
} from '@pieltec/contracts';
import { apiFetch, apiMutate } from '../../lib/csrf-fetch';
import { mensagemLegivelDoErro } from '../../lib/omie-erro-api';
import { opcoesDeAprovacao, papelInicial } from '../../lib/omie-aprovacao-piloto';
import { obterSessaoAtual } from '../../lib/session';
import { TablePagination } from '../../components/ui/table-pagination';
import { useClientPagination } from '../../hooks/use-client-pagination';
import { ResponsiveTableRow, useTabelaEmContextoTablet } from '../../components/ui/responsive-table-row';
import { StatusIndicator } from '../../components/ui/status-indicator';
import { CampoSelect } from '../../components/ui/campo-select';

function mensagemDaResposta(valor: unknown, padrao: string): string {
  if (typeof valor !== 'object' || valor === null) return padrao;
  const mensagem = (valor as Record<string, unknown>).message;
  return typeof mensagem === 'string' ? mensagem : padrao;
}

export function OmiePendenciasPage() {
  const [pendencias, setPendencias] = useState<OmiePendencia[]>([]);
  const [statusPiloto, setStatusPiloto] = useState<OmiePilotoStatusResponse | null>(null);
  const [carregando, setCarregando] = useState(true);
  // L-60: erro de carga (ex.: 403) aparece como mensagem legível e nunca como lista vazia.
  const [erroPendencias, setErroPendencias] = useState<string | null>(null);
  const [erroPiloto, setErroPiloto] = useState<string | null>(null);
  // Permissões da sessão (null até carregar): definem quais ações a pessoa vê.
  const [permissoes, setPermissoes] = useState<readonly string[] | null>(null);
  const [filtroEstado, setFiltroEstado] = useState<string>('TODOS');
  const [mensagemFeedback, setMensagemFeedback] = useState<{ tipo: 'sucesso' | 'erro'; texto: string } | null>(null);
  const tabelaEmTablet = useTabelaEmContextoTablet();

  // Modais de ação
  const [modalReenvio, setModalReenvio] = useState<{
    aberto: boolean;
    retornoId: string;
    etapa: EtapaFiscalTipo;
    justificativa: string;
    enviando: boolean;
  }>({
    aberto: false,
    retornoId: '',
    etapa: 'NFE',
    justificativa: '',
    enviando: false,
  });

  const [modalPilotoControle, setModalPilotoControle] = useState<{
    aberto: boolean;
    acao: 'pausar' | 'liberar';
    justificativa: string;
    enviando: boolean;
  }>({
    aberto: false,
    acao: 'pausar',
    justificativa: '',
    enviando: false,
  });

  const [modalIncluirCliente, setModalIncluirCliente] = useState<{
    aberto: boolean;
    clienteId: string;
    justificativa: string;
    enviando: boolean;
  }>({
    aberto: false,
    clienteId: '',
    justificativa: '',
    enviando: false,
  });

  const [modalAprovarCliente, setModalAprovarCliente] = useState<{
    aberto: boolean;
    clienteId: string;
    papel: 'ADMIN' | 'GESTAO';
    evidenceHash: string;
    justificativa: string;
    enviando: boolean;
  }>({
    aberto: false,
    clienteId: '',
    papel: 'ADMIN',
    evidenceHash: '',
    justificativa: '',
    enviando: false,
  });

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const [resPend, resPiloto] = await Promise.allSettled([
        apiFetch<OmiePendencia[]>('/api/v1/omie/pendencias'),
        apiFetch<OmiePilotoStatusResponse>('/api/v1/omie/piloto'),
      ]);
      if (resPend.status === 'fulfilled') {
        setPendencias(resPend.value || []);
        setErroPendencias(null);
      } else {
        setPendencias([]);
        setErroPendencias(mensagemLegivelDoErro(resPend.reason, 'Falha ao carregar as pendências fiscais da Omie.'));
      }
      if (resPiloto.status === 'fulfilled') {
        setStatusPiloto(resPiloto.value || null);
        setErroPiloto(null);
      } else {
        setStatusPiloto(null);
        setErroPiloto(mensagemLegivelDoErro(resPiloto.reason, 'Falha ao carregar o piloto da integração Omie.'));
      }
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    void carregarDados();
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo) setPermissoes(sessao.autenticado ? sessao.usuario.permissions : []);
    });
    return () => {
      vivo = false;
    };
  }, []);

  const temPermissao = (chave: string): boolean => permissoes?.includes(chave) ?? false;
  const podeReenviar = temPermissao('omie:operar') || temPermissao('omie:reenviar');
  const podeAdministrar = temPermissao('omie:administrar');
  // Papéis que esta pessoa pode registrar: ADMIN exige omie:administrar; GESTAO exige omie:aprovar-piloto (L-60, D-13).
  const { papeis: papeisPermitidos, somenteGestao } = opcoesDeAprovacao(permissoes);

  const pendenciasFiltradas = pendencias.filter((p) => {
    if (filtroEstado === 'TODOS') return true;
    return p.estado === filtroEstado;
  });
  const pendenciasPagination = useClientPagination(pendenciasFiltradas, [filtroEstado]);
  const clientesPiloto = statusPiloto?.clientes ?? [];
  const clientesPagination = useClientPagination(clientesPiloto);

  const handleReenviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalReenvio.justificativa || modalReenvio.justificativa.trim().length < 5) {
      setMensagemFeedback({ tipo: 'erro', texto: 'A justificativa de reenvio deve ter ao menos 5 caracteres.' });
      return;
    }

    setModalReenvio((prev) => ({ ...prev, enviando: true }));
    try {
      const res = await apiMutate(`/api/v1/omie/faturamentos/${modalReenvio.retornoId}/reenvios`, {
        method: 'POST',
        body: {
          etapa: modalReenvio.etapa,
          justificativa: modalReenvio.justificativa,
        },
      });

      if (!res.ok) {
        const data: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDaResposta(data, 'Falha ao solicitar reenvio.'));
      }

      setMensagemFeedback({ tipo: 'sucesso', texto: 'Reenvio manual enfileirado com sucesso.' });
      setModalReenvio({ aberto: false, retornoId: '', etapa: 'NFE', justificativa: '', enviando: false });
      await carregarDados();
    } catch (err) {
      setMensagemFeedback({
        tipo: 'erro',
        texto: err instanceof Error ? err.message : 'Falha ao solicitar reenvio.',
      });
      setModalReenvio((prev) => ({ ...prev, enviando: false }));
    }
  };

  const handleAlterarControlePiloto = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalPilotoControle.justificativa || modalPilotoControle.justificativa.trim().length < 5) {
      setMensagemFeedback({ tipo: 'erro', texto: 'A justificativa deve ter ao menos 5 caracteres.' });
      return;
    }

    setModalPilotoControle((prev) => ({ ...prev, enviando: true }));
    const endpoint = modalPilotoControle.acao === 'pausar' ? '/api/v1/omie/piloto/pausas' : '/api/v1/omie/piloto/liberacoes';

    try {
      const res = await apiMutate(endpoint, {
        method: 'POST',
        body: { justificativa: modalPilotoControle.justificativa },
      });

      if (!res.ok) {
        const data: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDaResposta(data, 'Falha ao alterar controle do piloto.'));
      }

      setMensagemFeedback({
        tipo: 'sucesso',
        texto: `Piloto ${modalPilotoControle.acao === 'pausar' ? 'pausado' : 'liberado'} com sucesso.`,
      });
      setModalPilotoControle({ aberto: false, acao: 'pausar', justificativa: '', enviando: false });
      await carregarDados();
    } catch (err) {
      setMensagemFeedback({
        tipo: 'erro',
        texto: err instanceof Error ? err.message : 'Falha ao alterar controle do piloto.',
      });
      setModalPilotoControle((prev) => ({ ...prev, enviando: false }));
    }
  };

  const handleIncluirCliente = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalIncluirCliente.clienteId || !modalIncluirCliente.justificativa.trim()) {
      const faltantes = [!modalIncluirCliente.clienteId ? 'Cliente autorizado' : null, !modalIncluirCliente.justificativa.trim() ? 'Justificativa' : null].filter(Boolean);
      setMensagemFeedback({ tipo: 'erro', texto: `Falta preencher em Incluir cliente no piloto: ${faltantes.join(' e ')}.` });
      if (!modalIncluirCliente.clienteId) document.getElementById('piloto-cliente')?.focus();
      else document.querySelector<HTMLElement>('[data-testid="input-justificativa-allowlist"]')?.focus();
      return;
    }

    setModalIncluirCliente((prev) => ({ ...prev, enviando: true }));
    try {
      const res = await apiMutate('/api/v1/omie/piloto/clientes', {
        method: 'POST',
        body: {
          clienteId: modalIncluirCliente.clienteId,
          justificativa: modalIncluirCliente.justificativa,
        },
      });

      if (!res.ok) {
        const data: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDaResposta(data, 'Falha ao incluir cliente.'));
      }

      setMensagemFeedback({ tipo: 'sucesso', texto: 'Cliente incluído na allowlist do piloto.' });
      setModalIncluirCliente({ aberto: false, clienteId: '', justificativa: '', enviando: false });
      await carregarDados();
    } catch (err) {
      setMensagemFeedback({
        tipo: 'erro',
        texto: err instanceof Error ? err.message : 'Falha ao incluir cliente.',
      });
      setModalIncluirCliente((prev) => ({ ...prev, enviando: false }));
    }
  };

  const handleAprovarCliente = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalAprovarCliente.evidenceHash || !modalAprovarCliente.justificativa) {
      setMensagemFeedback({ tipo: 'erro', texto: 'Hash da evidência e justificativa são obrigatórios.' });
      return;
    }

    setModalAprovarCliente((prev) => ({ ...prev, enviando: true }));
    try {
      const res = await apiMutate(`/api/v1/omie/piloto/clientes/${modalAprovarCliente.clienteId}/aprovacoes`, {
        method: 'POST',
        body: {
          papel: modalAprovarCliente.papel,
          evidenceHash: modalAprovarCliente.evidenceHash,
          justificativa: modalAprovarCliente.justificativa,
        },
      });

      if (!res.ok) {
        const data: unknown = await res.json().catch(() => null);
        throw new Error(mensagemDaResposta(data, 'Falha ao registrar aprovação.'));
      }

      setMensagemFeedback({ tipo: 'sucesso', texto: `Aprovação ${modalAprovarCliente.papel} registrada com sucesso.` });
      setModalAprovarCliente({ aberto: false, clienteId: '', papel: 'ADMIN', evidenceHash: '', justificativa: '', enviando: false });
      await carregarDados();
    } catch (err) {
      setMensagemFeedback({
        tipo: 'erro',
        texto: err instanceof Error ? err.message : 'Falha ao registrar aprovação.',
      });
      setModalAprovarCliente((prev) => ({ ...prev, enviando: false }));
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <OmiePendenciasCentralPanel />
      {/* Cabeçalho */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Shield className="w-7 h-7 text-primary" />
            Integração Omie — Central de Pendências e Piloto
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Governança fail-closed, recuperação operacional auditada e controle por allowlist (D-12..D-16).
          </p>
        </div>
        <button
          type="button"
          onClick={() => void carregarDados()}
          disabled={carregando}
          className="inline-flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-md text-sm font-medium hover:bg-secondary/80 focus:ring-2 focus:ring-primary disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${carregando ? 'animate-spin' : ''}`} />
          Atualizar Dados
        </button>
      </div>

      {/* Alerta de Feedback */}
      {mensagemFeedback && (
        <div
          role="alert"
          aria-live="polite"
          className={`p-4 rounded-md flex items-center justify-between gap-3 text-sm ${
            mensagemFeedback.tipo === 'sucesso'
              ? 'bg-emerald-950/40 text-emerald-300 border border-emerald-800'
              : 'bg-destructive/15 text-destructive border border-destructive/30'
          }`}
        >
          <span>{mensagemFeedback.texto}</span>
          <button
            type="button"
            onClick={() => setMensagemFeedback(null)}
            className="text-xs underline hover:opacity-80"
          >
            Fechar
          </button>
        </div>
      )}

      {/* Banner de Estado Global do Piloto (D-14) */}
      {statusPiloto && (
        <div
          className={`p-4 rounded-lg border flex flex-col md:flex-row items-start md:items-center justify-between gap-4 ${
            statusPiloto.controle.estado === 'LIBERADO'
              ? 'bg-emerald-950/30 border-emerald-800/60 text-emerald-200'
              : 'bg-amber-950/40 border-amber-800/60 text-amber-200'
          }`}
        >
          <div className="flex items-center gap-3">
            {statusPiloto.controle.estado === 'LIBERADO' ? (
              <PlayCircle className="w-8 h-8 text-emerald-400 shrink-0" />
            ) : (
              <PauseCircle className="w-8 h-8 text-amber-400 shrink-0" />
            )}
            <div>
              <div className="font-semibold text-base flex items-center gap-2">
                <span>Piloto Fiscal Omie: {statusPiloto.controle.estado}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-background/40 border border-current font-mono">
                  v{statusPiloto.controle.versao}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Motivo: {statusPiloto.controle.motivo || 'Nenhum motivo registrado.'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end md:self-auto">
            {!podeAdministrar ? null : statusPiloto.controle.estado === 'LIBERADO' ? (
              <button
                type="button"
                data-testid="btn-pausar-piloto"
                onClick={() => setModalPilotoControle({ aberto: true, acao: 'pausar', justificativa: '', enviando: false })}
                className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-medium focus:ring-2 focus:ring-amber-400"
              >
                Pausar Piloto Global
              </button>
            ) : (
              <button
                type="button"
                data-testid="btn-liberar-piloto"
                onClick={() => setModalPilotoControle({ aberto: true, acao: 'liberar', justificativa: '', enviando: false })}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-medium focus:ring-2 focus:ring-emerald-400"
              >
                Liberar Piloto Global
              </button>
            )}
          </div>
        </div>
      )}

      {/* Cartões de Contagem */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 bg-card rounded-lg border border-border">
          <div className="text-xs text-muted-foreground font-medium">Total de Pendências</div>
          <div className="text-2xl font-bold text-foreground mt-1">{pendencias.length}</div>
        </div>
        <div className="p-4 bg-card rounded-lg border border-border">
          <div className="text-xs text-amber-400 font-medium">Ação Necessária</div>
          <div className="text-2xl font-bold text-amber-300 mt-1">
            {pendencias.filter((p) => p.estado === 'ACAO_NECESSARIA').length}
          </div>
        </div>
        <div className="p-4 bg-card rounded-lg border border-border">
          <div className="text-xs text-emerald-400 font-medium">Clientes na Allowlist</div>
          <div className="text-2xl font-bold text-emerald-300 mt-1">
            {statusPiloto?.clientes.filter((c: OmiePilotoCliente) => c.ativo).length ?? 0}
          </div>
        </div>
      </div>

      {/* Tabela de Pendências Fiscais (D-12) */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="p-4 border-b border-border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Fila de Pendências Fiscais
            </h2>
            <p className="text-xs text-muted-foreground">
              Operações que exigem intervenção, reconciliação ou autorização.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Filtrar:</span>
            <CampoSelect
              aria-label="Filtrar pendências fiscais por estado"
              size="sm"
              value={filtroEstado}
              onValueChange={setFiltroEstado}
              options={[
                { value: 'TODOS', label: 'Todos os Estados' },
                { value: 'ACAO_NECESSARIA', label: 'Ação Necessária' },
                { value: 'EMITINDO_NFE', label: 'Emitindo NF-e' },
                { value: 'LANCANDO_TITULO', label: 'Lançando Título' },
                { value: 'AGUARDANDO_FILA', label: 'Aguardando Fila' },
              ]}
              className="w-48 text-xs"
            />
          </div>
        </div>

        <div>
          <table className="w-full text-left text-xs text-foreground">
            <thead className="bg-muted/50 border-b border-border text-muted-foreground uppercase font-medium">
              <tr>
                {!tabelaEmTablet && <th className="p-3">Retorno</th>}
                <th className="p-3">Cliente</th>
                {!tabelaEmTablet && <th className="p-3">Etapa</th>}
                <th className="p-3">Estado</th>
                {!tabelaEmTablet && <th className="p-3">Diagnóstico</th>}
                <th className="p-3">Próxima Ação</th>
                <th className="p-3 text-right">Ação</th>
                {tabelaEmTablet && <th className="p-2 text-right"><span className="sr-only">Detalhes</span></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {erroPendencias ? (
                <tr>
                  <td colSpan={tabelaEmTablet ? 5 : 7} role="alert" data-testid="erro-pendencias" className="p-6 text-center text-destructive">
                    {erroPendencias}
                  </td>
                </tr>
              ) : pendenciasFiltradas.length === 0 ? (
                <tr>
                  <td colSpan={tabelaEmTablet ? 5 : 7} className="p-6 text-center text-muted-foreground">
                    Nenhuma pendência fiscal encontrada.
                  </td>
                </tr>
              ) : (
                pendenciasPagination.pageItems.map((p) => {
                  const exigeAcao = p.estado === 'ACAO_NECESSARIA';
                  const acao = exigeAcao && podeReenviar ? (
                    <button
                      type="button"
                      data-testid={`btn-reenviar-${p.retornoId}`}
                      onClick={() => setModalReenvio({ aberto: true, retornoId: p.retornoId, etapa: p.etapaPendente || 'NFE', justificativa: '', enviando: false })}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary text-primary-foreground rounded text-xs font-medium hover:bg-primary/90 focus:ring-2 focus:ring-primary"
                    >
                      <Send className="w-3 h-3" />
                      Reenviar
                    </button>
                  ) : <span className="text-xs text-muted-foreground">Sem ação</span>;

                  return (
                    <ResponsiveTableRow
                      key={p.retornoId}
                      rowId={`pendencia-${p.retornoId}`}
                      className="hover:bg-muted/30"
                      primaryCellIds={['cliente', 'estado', 'proxima-acao', 'acao']}
                      cells={[
                        { id: 'retorno', label: 'Retorno', content: <span className="font-mono font-medium break-all">{p.retornoId.slice(0, 8)}...</span> },
                        { id: 'cliente', label: 'Cliente', content: <span className="break-words">{p.clienteNome}</span> },
                        { id: 'etapa', label: 'Etapa', content: <StatusIndicator tone="neutral" label={p.etapaPendente || 'N/A'} /> },
                        { id: 'estado', label: 'Estado', content: <StatusIndicator tone={exigeAcao ? 'warning' : 'info'} icon={exigeAcao ? <AlertTriangle className="size-3" aria-hidden="true" /> : <RefreshCw className="size-3 animate-spin" aria-hidden="true" />} label={p.estado} /> },
                        { id: 'diagnostico', label: 'Diagnóstico', content: <span className="font-mono text-xs text-muted-foreground">{p.diagnosticoCodigo || 'NENHUM'}</span> },
                        { id: 'proxima-acao', label: 'Próxima ação', content: <span className="break-words text-xs text-muted-foreground">{p.proximaAcao}</span> },
                        { id: 'acao', label: 'Ação', content: acao, className: 'text-right' },
                      ]}
                    />
                  );
                })
              )}
            </tbody>
          </table>
          {pendenciasFiltradas.length > 0 && (
            <TablePagination
              page={pendenciasPagination.page}
              pageSize={pendenciasPagination.pageSize}
              totalItems={pendenciasFiltradas.length}
              itemLabel="pendências"
              onPageChange={pendenciasPagination.setPage}
              onPageSizeChange={pendenciasPagination.setPageSize}
            />
          )}
        </div>
      </div>

      {/* Seção da Allowlist e Governança do Piloto (D-13) */}
      <div className="bg-card rounded-lg border border-border p-4 space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-border pb-3">
          <div>
            <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
              Allowlist do Piloto e Aprovações Formais (D-13)
            </h2>
            <p className="text-xs text-muted-foreground">
              Apenas clientes com dupla aprovação formal ativa (ADMIN e GESTÃO por usuários distintos) participam do piloto.
            </p>
          </div>

          {podeAdministrar && (
            <button
              type="button"
              data-testid="btn-adicionar-cliente-allowlist"
              onClick={() => setModalIncluirCliente({ aberto: true, clienteId: '', justificativa: '', enviando: false })}
              className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-medium hover:bg-primary/90 focus:ring-2 focus:ring-primary"
            >
              Autorizar cliente no piloto
            </button>
          )}
        </div>

        <div>
          <table className="w-full text-left text-xs text-foreground">
            <thead className="bg-muted/50 border-b border-border text-muted-foreground uppercase font-medium">
              <tr>
                <th className="p-3">Cliente ID</th>
                <th className="p-3">Situação</th>
                {!tabelaEmTablet && <th className="p-3">Aprovação ADMIN</th>}
                {!tabelaEmTablet && <th className="p-3">Aprovação GESTÃO</th>}
                <th className="p-3 text-right">Ações</th>
                {tabelaEmTablet && <th className="p-2 text-right"><span className="sr-only">Detalhes</span></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {erroPiloto ? (
                <tr>
                  <td colSpan={tabelaEmTablet ? 4 : 5} role="alert" data-testid="erro-piloto" className="p-6 text-center text-destructive">
                    {erroPiloto}
                  </td>
                </tr>
              ) : !statusPiloto || statusPiloto.clientes.length === 0 ? (
                <tr>
                  <td colSpan={tabelaEmTablet ? 4 : 5} className="p-6 text-center text-muted-foreground">
                    Nenhum cliente cadastrado na allowlist do piloto.
                  </td>
                </tr>
              ) : (
                clientesPagination.pageItems.map((c: OmiePilotoCliente) => {
                  const aprAdmin = c.aprovacoes.find((a: OmiePilotoAprovacao) => a.papel === 'ADMIN');
                  const aprGestao = c.aprovacoes.find((a: OmiePilotoAprovacao) => a.papel === 'GESTAO');

                  const estadoAprovacao = (aprovacao: OmiePilotoAprovacao | undefined) => (
                    <StatusIndicator tone={aprovacao ? 'success' : 'warning'} icon={aprovacao ? <CheckCircle2 className="size-3.5" aria-hidden="true" /> : <AlertCircle className="size-3.5" aria-hidden="true" />} label={aprovacao ? 'Aprovado' : 'Pendente'} />
                  );
                  const acoes = (
                    <div className="space-x-2">
                        {c.ativo && (
                          <>
                            {papeisPermitidos.length > 0 && (
                              <button
                                type="button"
                                data-testid={`btn-aprovar-${c.clienteId}`}
                                onClick={() =>
                                  setModalAprovarCliente({
                                    aberto: true,
                                    clienteId: c.clienteId,
                                    papel: papelInicial(
                                      papeisPermitidos,
                                      c.aprovacoes.map((a: OmiePilotoAprovacao) => a.papel),
                                    ),
                                    evidenceHash: '',
                                    justificativa: '',
                                    enviando: false,
                                  })
                                }
                                className="px-2 py-1 bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded text-xs font-medium"
                              >
                                {somenteGestao ? 'Aprovar como Gestão' : 'Registrar Aprovação'}
                              </button>
                            )}
                            {podeAdministrar && (
                            <button
                              type="button"
                              data-testid={`btn-revogar-${c.clienteId}`}
                              onClick={() => {
                                void (async () => {
                                  if (!window.confirm('Confirma a revogação deste cliente do piloto?')) return;
                                  try {
                                    const res = await apiMutate(`/api/v1/omie/piloto/clientes/${c.clienteId}`, {
                                      method: 'DELETE',
                                      body: { justificativa: 'Revogação operacional' },
                                    });
                                    if (!res.ok) {
                                      const data: unknown = await res.json().catch(() => null);
                                      throw new Error(mensagemDaResposta(data, 'Falha ao revogar cliente do piloto.'));
                                    }
                                    setMensagemFeedback({ tipo: 'sucesso', texto: 'Cliente revogado do piloto.' });
                                    await carregarDados();
                                  } catch (err) {
                                    setMensagemFeedback({
                                      tipo: 'erro',
                                      texto: err instanceof Error ? err.message : 'Falha ao revogar cliente do piloto.',
                                    });
                                  }
                                })();
                              }}
                              className="px-2 py-1 bg-destructive/15 text-destructive hover:bg-destructive/25 rounded text-xs font-medium"
                            >
                              Revogar
                            </button>
                            )}
                          </>
                        )}
                    </div>
                  );
                  return (
                    <ResponsiveTableRow
                      key={c.clienteId}
                      rowId={`cliente-${c.clienteId}`}
                      className="hover:bg-muted/30"
                      primaryCellIds={['cliente', 'situacao', 'acoes']}
                      cells={[
                        { id: 'cliente', label: 'Cliente ID', content: <span className="font-mono font-medium break-all">{c.clienteId}</span> },
                        { id: 'situacao', label: 'Situação', content: <StatusIndicator tone={c.ativo ? 'success' : 'danger'} label={c.ativo ? 'ATIVO' : 'REVOGADO'} /> },
                        { id: 'admin', label: 'Aprovação ADMIN', content: estadoAprovacao(aprAdmin) },
                        { id: 'gestao', label: 'Aprovação GESTÃO', content: estadoAprovacao(aprGestao) },
                        { id: 'acoes', label: 'Ações', content: acoes, className: 'text-right' },
                      ]}
                    />
                  );
                })
              )}
            </tbody>
          </table>
          {clientesPiloto.length > 0 && (
            <TablePagination
              page={clientesPagination.page}
              pageSize={clientesPagination.pageSize}
              totalItems={clientesPiloto.length}
              itemLabel="clientes"
              onPageChange={clientesPagination.setPage}
              onPageSizeChange={clientesPagination.setPageSize}
            />
          )}
        </div>
      </div>

      {/* Modal de Reenvio Manual (D-07) */}
      {modalReenvio.aberto && (
        <CenteredDialog title="Solicitar reenvio manual" onClose={() => setModalReenvio((prev) => ({ ...prev, aberto: false }))}>
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Solicitar reenvio manual
            </h3>
            <p className="text-xs text-muted-foreground">
              O reenvio gera uma nova tentativa auditada mantendo a chave de idempotência do retorno.
            </p>

            <form onSubmit={(evento) => void handleReenviar(evento)} className="space-y-3">
              <div>
                <label htmlFor="reenvio-etapa" className="block text-xs font-medium text-foreground mb-1">Etapa:</label>
                <CampoSelect
                  id="reenvio-etapa"
                  value={modalReenvio.etapa}
                  onValueChange={(valor) => setModalReenvio((prev) => ({ ...prev, etapa: valor === 'TITULO' ? 'TITULO' : 'NFE' }))}
                  options={[
                    { value: 'NFE', label: 'NF-e de Retorno (Produtos/NF-e)' },
                    { value: 'TITULO', label: 'Título Financeiro (Contas a Receber)' },
                  ]}
                  className="text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1" htmlFor="field-omie-pendencias-page-29418">
                  Justificativa Operacional (obrigatória, mín. 5 chars):
                </label>
                <textarea id="field-omie-pendencias-page-29418"
                  data-testid="input-justificativa-reenvio"
                  required
                  rows={3}
                  value={modalReenvio.justificativa}
                  onChange={(e) => setModalReenvio((prev) => ({ ...prev, justificativa: e.target.value }))}
                  placeholder="Ex.: Falha de rede restabelecida, solicitando reenvio autorizado."
                  className="w-full bg-background border border-input rounded p-2 text-xs text-foreground"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setModalReenvio((prev) => ({ ...prev, aberto: false }))}
                  className="px-3 py-1.5 bg-secondary text-secondary-foreground rounded text-xs"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="btn-confirmar-reenvio"
                  disabled={modalReenvio.enviando}
                  className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-medium disabled:opacity-50"
                >
                  {modalReenvio.enviando ? 'Enviando...' : 'Confirmar Reenvio'}
                </button>
              </div>
            </form>
          </div>
        </CenteredDialog>
      )}

      {/* Modal de Controle Global do Piloto (Pausar / Liberar) */}
      {modalPilotoControle.aberto && (
        <CenteredDialog title="Detalhes da operação" onClose={() => setModalPilotoControle((prev) => ({ ...prev, aberto: false }))}>
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-primary" />
              {modalPilotoControle.acao === 'pausar' ? 'Pausar Piloto Omie' : 'Liberar Piloto Omie'}
            </h3>
            <p className="text-xs text-muted-foreground">
              Esta ação afeta a emissão automática de todos os retornos. Exige justificativa formal auditada.
            </p>

            <form onSubmit={(evento) => void handleAlterarControlePiloto(evento)} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1" htmlFor="field-omie-pendencias-page-31949">
                  Justificativa Formal (mín. 5 caracteres):
                </label>
                <textarea id="field-omie-pendencias-page-31949"
                  data-testid="input-justificativa-piloto"
                  required
                  rows={3}
                  value={modalPilotoControle.justificativa}
                  onChange={(e) => setModalPilotoControle((prev) => ({ ...prev, justificativa: e.target.value }))}
                  placeholder="Ex.: Pausa preventiva para auditoria fiscal de fechamento."
                  className="w-full bg-background border border-input rounded p-2 text-xs text-foreground"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setModalPilotoControle((prev) => ({ ...prev, aberto: false }))}
                  className="px-3 py-1.5 bg-secondary text-secondary-foreground rounded text-xs"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="btn-confirmar-controle-piloto"
                  disabled={modalPilotoControle.enviando}
                  className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-medium disabled:opacity-50"
                >
                  {modalPilotoControle.enviando
                    ? modalPilotoControle.acao === 'pausar' ? 'Pausando piloto...' : 'Liberando piloto...'
                    : modalPilotoControle.acao === 'pausar' ? 'Confirmar pausa do piloto' : 'Confirmar liberação do piloto'}
                </button>
              </div>
            </form>
          </div>
        </CenteredDialog>
      )}

      {/* Modal de Inclusão de Cliente na Allowlist */}
      {modalIncluirCliente.aberto && (
        <CenteredDialog title="Autorizar cliente no piloto" onClose={() => setModalIncluirCliente((prev) => ({ ...prev, aberto: false }))}>
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-primary" />
              Autorizar cliente no piloto
            </h3>

            <form onSubmit={(evento) => void handleIncluirCliente(evento)} className="space-y-3">
              {(!modalIncluirCliente.clienteId || !modalIncluirCliente.justificativa.trim()) && <p className="text-sm" aria-live="polite">Falta preencher neste formulário: {[!modalIncluirCliente.clienteId ? 'Cliente autorizado' : null, !modalIncluirCliente.justificativa.trim() ? 'Justificativa de Inclusão' : null].filter(Boolean).join(' e ')}.</p>}
              <div>
                <label htmlFor="piloto-cliente" className="text-sm font-medium">Cliente autorizado *</label><LookupCombobox id="piloto-cliente" endpoint="/api/v1/omie/lookups/clientes" label="Cliente" value={modalIncluirCliente.clienteId} onChange={value => setModalIncluirCliente(prev => ({ ...prev, clienteId: value }))} required />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1" htmlFor="field-omie-pendencias-page-34886">
                  Justificativa de Inclusão:
                </label>
                <textarea id="field-omie-pendencias-page-34886"
                  data-testid="input-justificativa-allowlist"
                  required
                  rows={2}
                  value={modalIncluirCliente.justificativa}
                  onChange={(e) => setModalIncluirCliente((prev) => ({ ...prev, justificativa: e.target.value }))}
                  placeholder="Ex.: Cliente aprovado para fase de testes controlados."
                  className="w-full bg-background border border-input rounded p-2 text-xs text-foreground"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setModalIncluirCliente((prev) => ({ ...prev, aberto: false }))}
                  className="px-3 py-1.5 bg-secondary text-secondary-foreground rounded text-xs"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="btn-confirmar-inclusao-allowlist"
                  disabled={modalIncluirCliente.enviando || !modalIncluirCliente.clienteId}
                  className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-medium disabled:opacity-50"
                >
                  {modalIncluirCliente.enviando ? 'Gravando...' : 'Adicionar'}
                </button>
              </div>
            </form>
          </div>
        </CenteredDialog>
      )}

      {/* Modal de Aprovação Formal de Cliente (ADMIN / GESTÃO) */}
      {modalAprovarCliente.aberto && (
        <CenteredDialog title="Registrar Aprovação Formal" onClose={() => setModalAprovarCliente((prev) => ({ ...prev, aberto: false }))}>
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              <FileCheck2 className="w-5 h-5 text-primary" />
              Registrar Aprovação Formal (D-13)
            </h3>

            <form onSubmit={(evento) => void handleAprovarCliente(evento)} className="space-y-3">
              <div>
                <label htmlFor="aprovacao-papel" className="block text-xs font-medium text-foreground mb-1">Papel da Aprovação:</label>
                {papeisPermitidos.length <= 1 ? (
                  <p data-testid="papel-aprovacao-fixo" className="w-full bg-muted/40 border border-input rounded p-2 text-xs text-foreground">
                    {modalAprovarCliente.papel === 'GESTAO'
                      ? 'GESTAO (Supervisão Operacional e Fiscal) — papel fixo da sua permissão'
                      : 'ADMIN (Infraestrutura / Governança Técnica)'}
                  </p>
                ) : (
                  <CampoSelect
                    id="aprovacao-papel"
                    data-testid="select-papel-aprovacao"
                    value={modalAprovarCliente.papel}
                    onValueChange={(valor) => setModalAprovarCliente((prev) => ({ ...prev, papel: valor === 'GESTAO' ? 'GESTAO' : 'ADMIN' }))}
                    options={[
                      { value: 'ADMIN', label: 'ADMIN (Infraestrutura / Governança Técnica)' },
                      { value: 'GESTAO', label: 'GESTAO (Supervisão Operacional e Fiscal)' },
                    ]}
                    className="text-xs"
                  />
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1" htmlFor="field-omie-pendencias-page-37914">
                  Hash SHA-256 da Evidência:
                </label>
                <input id="field-omie-pendencias-page-37914"
                  data-testid="input-evidence-hash"
                  type="text"
                  required
                  value={modalAprovarCliente.evidenceHash}
                  onChange={(e) => setModalAprovarCliente((prev) => ({ ...prev, evidenceHash: e.target.value }))}
                  placeholder="Hash SHA-256 de 64 caracteres"
                  className="w-full bg-background border border-input rounded p-2 text-xs font-mono text-foreground"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1" htmlFor="field-omie-pendencias-page-38627">
                  Justificativa da Aprovação:
                </label>
                <textarea id="field-omie-pendencias-page-38627"
                  data-testid="input-justificativa-aprovacao"
                  required
                  rows={2}
                  value={modalAprovarCliente.justificativa}
                  onChange={(e) => setModalAprovarCliente((prev) => ({ ...prev, justificativa: e.target.value }))}
                  placeholder="Ex.: Homologação formal aprovada pelo setor fiscal."
                  className="w-full bg-background border border-input rounded p-2 text-xs text-foreground"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setModalAprovarCliente((prev) => ({ ...prev, aberto: false }))}
                  className="px-3 py-1.5 bg-secondary text-secondary-foreground rounded text-xs"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="btn-confirmar-aprovacao"
                  disabled={modalAprovarCliente.enviando}
                  className="px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-medium disabled:opacity-50"
                >
                  {modalAprovarCliente.enviando ? 'Gravando...' : 'Confirmar Aprovação'}
                </button>
              </div>
            </form>
          </div>
        </CenteredDialog>
      )}
    </div>
  );
}
